// ニュースのブックマーク。端末ローカル保存（localStorage・サーバー非同期）。
//
// 記事のIDだけでなく**スナップショットを保存する**。
// サーバー側の記事は保持期間(90日)で消えるが、手元に残るのがlocal保存の利点であるため。
//
// この型は将来サーバー同期する場合の user_news_bookmarks テーブルと同形にしてある。
// 移行時の変換コストをゼロにするためなので、カラムを増やすときは両方合わせること。
import type { NewsArticle } from '../types/News';

const KEY = 'news_bookmarks_v1';

// 上限。超えたら古いものから捨てる（localStorage の容量制限を踏まないため）
const MAX_ITEMS = 200;

export interface NewsBookmark {
  article_id: number;
  title: string;
  url: string;
  image_url: string | null;
  source_name: string;
  published_at: string;
  saved_at: string;
}

export const readBookmarks = (): NewsBookmark[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // 壊れた値が入っていても落とさない
    return [];
  }
};

const write = (items: NewsBookmark[]): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
  } catch {
    /* localStorage 利用不可・容量超過時は黙って無視 */
  }
};

export const isBookmarked = (articleId: number): boolean =>
  readBookmarks().some((b) => b.article_id === articleId);

export const addBookmark = (article: NewsArticle): NewsBookmark[] => {
  const items = readBookmarks().filter((b) => b.article_id !== article.id);
  const next: NewsBookmark[] = [
    {
      article_id: article.id,
      title: article.title,
      url: article.url,
      image_url: article.image?.url ?? null,
      source_name: article.source.name,
      published_at: article.published_at,
      saved_at: new Date().toISOString(),
    },
    ...items,
  ];
  write(next);
  return next.slice(0, MAX_ITEMS);
};

export const removeBookmark = (articleId: number): NewsBookmark[] => {
  const next = readBookmarks().filter((b) => b.article_id !== articleId);
  write(next);
  return next;
};

/** 保存をすべて消す */
export const clearBookmarks = (): NewsBookmark[] => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* 無視 */
  }
  return [];
};

export const toggleBookmark = (article: NewsArticle): boolean => {
  if (isBookmarked(article.id)) {
    removeBookmark(article.id);
    return false;
  }
  addBookmark(article);
  return true;
};
